<template lang="pug">
.chat-message-wrapper(:class='{"is-latest": isLatest, "is-me": false}')
  .chat-message-container(v-if='!editing', :class='{"is-latest": isLatest}')
    .chat-icon
      img.thread-comment__user-icon.a-user-icon(
        :src='comment.user.avatar_url',
        :title='comment.user.icon_title')
    header.chat-header
      h2.chat-header_name
        | {{ comment.user.name }}
      time.chat-header_time(
        :class='{ "is-active": activating }',
        :datetime='commentableCreatedAt')
        | {{ createdAt }}
      time.chat-header_time(v-if="comment.created_at != comment.updated_at")
        | （編集済み {{ updatedAt }}）
    .chat-body
      .chat-message__description
        .a-chat-text.is-md(v-html='markdownDescription').js-contents-image
      .chat-body_actions.chat-body_actions__reactions(v-if="currentUser.writable")
        reaction(
          v-bind:reactionable='comment',
          v-bind:currentUser='currentUser',
          v-bind:reactionableId='reactionableId'
        )
      .chat-body_actions(v-if="comment.user.id == currentUser.id && currentUser.writable")
        ul.chat-body_actions_buttons
          // 編集アイコン
          li.chat-options__item
            .mentor-comment-actions__muted-action.is-de(
              @click='editComment($event)',
              :class="{ 'is-processing': isUpdating || isDeleting }"
            )
              i.fa-solid.fa-pen(
                :class='{ "is-latest": isLatest }'
              )
          // 削除アイコン
          li.chat-options__item
            .mentor-comment-actions__muted-action.is-de(
              @click='deleteComment',
              :class="{ 'is-processing': isDeleting }"
            )
              i.fa-solid.fa-trash
      // リアクションスタンプ
      .chat-reaction
        .thread-comment__reactions(:class='{ "thread-comment__reactions__hasReaction": hasAnyReaction }')
          reactions(
            v-bind:reactionable='comment',
            v-bind:currentUser='currentUser',
            v-bind:reactionableId='reactionableId'
          )
        component(
          :is='footprintComponent',
          :footprintableId='`${comment.id}`',
          :footprintableType='`Comment`',
        )
  // メッセージ編集用のフォーム
  .chat-message-edit-container(v-show='editing')
    .a-form-tabs.js-tabs
      .a-form-tabs__tab.js-tabs__tab(
        :class='{ "is-active": isActive("comment") }',
        @click='changeActiveTab("comment")')
        | コメント
      .a-form-tabs__tab.js-tabs__tab(
        :class='{ "is-active": isActive("preview") }',
        @click='changeActiveTab("preview")')
        | プレビュー
    .a-markdown-input.js-markdown-parent
      .a-markdown-input__inner.js-tabs__content(
        :class='{ "is-active": isActive("comment") }')
        .form-textarea
          .form-textarea__body
            textarea.a-text-input.a-markdown-input__textarea(
              v-model='description',
              name='new_comment[description]'
              :id='`js-comment-${this.comment.id}`',
              :data-preview='`#js-comment-preview-${this.comment.id}`',
              :data-input='`#js-comment-file-input-${this.comment.id}`',
              @compositionstart="isComposing = true",
              @compositionend="isComposing = false",
              @keydown.stop='handleKeyDown',
            )
          .chat-form-footer
            .mentor-comment-actions
              ul.mentor-comment-actions__items
                // ファイル添付
                li.card-main-actions__item.is-sub
                  label.mentor-comment-actions__muted-action.is-de
                    i.fa-solid.fa-paperclip
                    input.new-mentor-comment-file-input(:id='`js-comment-file-input-${this.comment.id}`', type='file', multiple hidden)
      .a-markdown-input__inner.js-tabs__content(
        :class='{ "is-active": isActive("preview") }')
        .a-chat-text.is-md.a-markdown-input__preview(
            :id='`js-comment-preview-${this.comment.id}`')
    hr.a-border-tint
    .card-footer
      .card-main-actions
        .card-main-actions__items
          .card-main-actions__item
            button.a-button.is-sm.is-primary.is-block(@click='updateComment',v-bind:disabled='!validation || isUpdating')
              | 更新
          .card-main-actions__item
            button.a-button.is-sm.is-secondary.is-block(@click='cancel')
              | キャンセル

</template>
<script>
import CSRF from 'csrf'
import Reaction from 'chat-reaction.vue'
import ChatReactions from 'chat-reactions.vue'
import MarkdownInitializer from 'markdown-initializer'
import TextareaInitializer from 'textarea-initializer'
import confirmUnload from 'confirm-unload'
import autosize from 'autosize'
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import role from 'role'
import ImageModalViewer from 'image-modal-viewer'
import Footprints from 'components/footprints.vue'
import FootprintTracker from 'components/FootprintTracker.vue'

dayjs.locale(ja)

export default {
  components: {
    reaction: Reaction,
    reactions: ChatReactions,
    footprints: Footprints,
    footprintTracker: FootprintTracker,
  },
  mixins: [confirmUnload, role],
  props: {
    comment: { type: Object, required: true },
    currentUser: { type: Object, required: true },
    isLatest: { type: Boolean, required: true }
  },
  data() {
    return {
      description: '',
      editing: false,
      isCopied: false,
      tab: 'comment',
      activating: false,
      isComposing: false, // 日本語入力中かどうかのフラグ
      isUpdating: false, // 更新処理中かどうかのフラグを追加
      isDeleting: false, // 削除処理中かどうかのフラグ
    }
  },
  computed: {
    footprintComponent() {
      return this.currentUser.operator ? 'footprints' : 'footprintTracker'
    },
    commentableCreatedAt() {
      return dayjs(this.comment.commentable.created_at).format()
    },
    createdAt() {
      return dayjs(this.comment.created_at).format('YYYY年MM月DD日(dd) HH:mm')
    },
    updatedAt() {
      return dayjs(this.comment.updated_at).format('YYYY年MM月DD日(dd) HH:mm')
    },
    markdownDescription() {
      const markdownInitializer = new MarkdownInitializer()
      return markdownInitializer.render(this.description)
    },
    updatedAt() {
      return dayjs(this.comment.updated_at).format('YYYY年MM月DD日(dd) HH:mm')
    },
    roleClass() {
      return `is-${this.comment.user.primary_role}`
    },
    validation() {
      return this.description.length > 0
    },
    reactionableId() {
      return `Comment_${this.comment.id}`
    },
    // リアクションが1件でも存在するかどうか
    hasAnyReaction() {
      return this.comment.reaction_count.some(el => el.count !== 0);
    },
  },
  created() {
    this.description = this.comment.description
  },
  updated() {
    // 非同期でDOM追加後、画像モーダル初期化イベントを実行
    const imageModalViewer = new ImageModalViewer()
    imageModalViewer.initialize()
  },
  mounted() {
    TextareaInitializer.initialize(`#js-comment-${this.comment.id}`)
    const commentAnchor = location.hash
    if (commentAnchor) {
      this.$nextTick(() => {
        location.replace(location.href)
      })
    }
  },
  methods: {
    isActive(tab) {
      return this.tab === tab
    },
    changeActiveTab(tab) {
      this.tab = tab
    },
    cancel() {
      this.description = this.comment.description
      this.editing = false
    },
    editComment() {
      this.editing = true
      this.$nextTick(function () {
        const textarea = document.querySelector(
          `#js-comment-${this.comment.id}`
        )
        autosize.update(textarea)
        $(`.comment-id-${this.comment.id}`).trigger('input')
      })
    },
    updateComment() {
      if (this.description.length < 1) {
        return null
      }
      const params = {
        comment: { description: this.description }
      }
      fetch(`/api/comments/${this.comment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': CSRF.getToken()
        },
        credentials: 'same-origin',
        redirect: 'manual',
        body: JSON.stringify(params)
      })
        .then(() => {
          this.$emit('update', this.description, this.comment.id)
          this.editing = false
        })
        .catch((error) => {
          console.warn(error)
        })
        .finally(() => {
          this.$nextTick(() => {
            Prism.highlightAll()
          })
        })
    },
    deleteComment() {
      if (window.confirm('削除してよろしいですか？')) {
        this.$emit('delete', this.comment.id)
      }
    },
    handleKeyDown(e) {
      // Safariでの日本語変換確定時（keyCodeが229）のエンターキー操作を無視
      if (e.keyCode === 229) return;

      // 日本語変換中ならブロック
      if (this.isComposing) return

      // Shift + Enter押下は改行する
      if (e.shiftKey && e.key === 'Enter') {
        const textarea = e.target
        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        this.description = this.description.substring(0, start) + '\n' + this.description.substring(end)
        this.$nextTick(() => {
          const position = start + 1
          textarea.selectionStart = position
          textarea.selectionEnd = position
        })
        e.preventDefault()
      }
      else if (e.key === 'Enter') {
        e.preventDefault()
        this.updateComment()
      }
    },
    copyCommentURLToClipboard(commentId) {
      const commentURL = location.href.split('#')[0] + '#comment_' + commentId
      const textBox = document.createElement('textarea')
      textBox.setAttribute('type', 'hidden')
      textBox.textContent = commentURL
      document.body.appendChild(textBox)
      textBox.select()
      document.execCommand('copy')
      document.body.removeChild(textBox)
      this.activating = true
      setTimeout(() => {
        this.activating = false
      }, 4000)
    }
  }
}
</script>
